import { STORYBOARD_INPUT_LINE, type SbFieldValue, type SbIvyState, type StoryboardInputRecord } from '@agentic-ivy/shared-types';
import { lookName } from '@/lib/studioB/takeover';

/** Fields the kit names itself, in the words the page used. */
const KNOWN: Record<string, string> = {
  'export.4k': 'Asked for a 4K version',
  'export.removeWatermark': 'Remove the watermark',
  'export.resolution': 'Export',
  feedback: 'Feedback',
};

const EDITS: Record<string, [string, string]> = {
  outline: ['change to the plan', 'changes to the plan'],
  cut: ['cut edit', 'cut edits'],
  program: ['program edit', 'program edits'],
};

/** `export.removeWatermark` → `Export remove watermark`. */
function humanize(name: string): string {
  const words = name.replace(/[._-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** A summary's label, unless it is the kit's fallback for a field the page gave no label (its name capitalised,
 * "Export.resolution"): then the field's known words. */
export function pageLabel(name: string, label: string): string {
  const unnamed = label === name.charAt(0).toUpperCase() + name.slice(1) && /[._A-Z-]/.test(name.slice(1));
  return unnamed ? KNOWN[name] ?? humanize(name) : label;
}

const isNote = (v: unknown): v is { for: string; text: string } =>
  !!v && typeof v === 'object' && typeof (v as { text?: unknown }).text === 'string';

function fieldLines(name: string, value: SbFieldValue): string[] {
  if (Array.isArray(value) && value.every(isNote)) return value.map((n) => `“${n.text}”`);
  if (value && typeof value === 'object' && Array.isArray((value as { ops?: unknown }).ops)) {
    const n = (value as { ops: unknown[] }).ops.length;
    const [one, many] = EDITS[name] ?? ['edit', 'edits'];
    return n ? [`${n} ${n === 1 ? one : many}`] : [];
  }
  if (typeof value === 'boolean') {
    if (KNOWN[name] && name.startsWith('export.')) return value ? [KNOWN[name]] : [];
    return [`${KNOWN[name] ?? humanize(name)}: ${value ? 'yes' : 'no'}`];
  }
  if (typeof value === 'string') {
    const v = value.trim();
    if (!v) return [];
    return [name === 'feedback' ? `“${v}”` : `${KNOWN[name] ?? humanize(name)}: ${v}`];
  }
  if (typeof value === 'number') return [`${KNOWN[name] ?? humanize(name)}: ${value}`];
  if (Array.isArray(value)) return value.length ? [`${KNOWN[name] ?? humanize(name)}: ${value.join(', ')}`] : [];
  return [humanize(name)];
}

/** What the person asked for, one line per thing, in their words where they wrote words. */
export function describeInput(input: StoryboardInputRecord): string[] {
  const names = input.touched.length ? input.touched : Object.keys(input.fields);
  const lines = names.flatMap((n) => (n in input.fields ? fieldLines(n, input.fields[n]) : []));
  if (input.lock) lines.unshift('Approved and asked to build');
  return lines.length ? lines : ['Sent without changes'];
}

/** The version that answered it: the first one published after it was applied. */
export function appliedIn(input: StoryboardInputRecord, versions: { version: number; createdAt: string }[]): number | null {
  if (input.status !== 'applied' || !input.appliedAt) return null;
  const at = new Date(input.appliedAt).getTime() - 60_000;
  const after = versions
    .filter((v) => v.version > input.storyboardVersion && new Date(v.createdAt).getTime() >= at)
    .sort((a, b) => a.version - b.version);
  return after[0]?.version ?? null;
}

/** What happened to it. */
export function statusWords(input: StoryboardInputRecord, versions: { version: number; createdAt: string }[]): string {
  if (input.status === 'pending') return 'Waiting for Ivy';
  if (input.status === 'superseded') return 'Replaced by a later note';
  const v = appliedIn(input, versions);
  return v ? `Applied in v${v}` : 'Applied';
}

/** The `[storyboard input …]` lines a send leads with, apart from what the person typed. */
export function splitStoryboardInputs(text: string): { inputs: { id: string; version: number }[]; rest: string } {
  const inputs: { id: string; version: number }[] = [];
  const rest: string[] = [];
  for (const line of text.split('\n')) {
    const m = line.trim().match(STORYBOARD_INPUT_LINE);
    if (m && m[0] === line.trim()) inputs.push({ id: m[1], version: Number(m[2]) });
    else rest.push(line);
  }
  return { inputs, rest: rest.join('\n').trim() };
}

type ChangeSource = Pick<StoryboardInputRecord, 'touched' | 'fields' | 'lock' | 'summaries' | 'images'>;
export interface ChangeRow { label: string; value: string; image: string | null }
export interface ChangeWords { what: string; value: string; image: string | null; rows: ChangeRow[] }

/** "Look: Night paper" → Look / Night paper; a line without a label is all value. */
function splitSummary(line: string): { label: string; value: string } {
  const m = line.match(/^([^:“”"]{1,40}):\s+(.+)$/);
  return m ? { label: m[1].trim(), value: m[2].trim() } : { label: '', value: line };
}

const isQuote = (r: ChangeRow) => !r.label && r.value.startsWith('“');
const NOTE_LABELS = /^(feedback|notes?)$/i;

function listWords(words: string[]): string {
  const all = words.length > 1 ? `${words.slice(0, -1).join(', ')} and ${words.at(-1)}` : words[0] ?? '';
  return all.charAt(0).toUpperCase() + all.slice(1);
}

/** What a storyboard send changed, in the page's own words where it sent them: the thread's "you" event. */
export function changeWords(input: ChangeSource): ChangeWords {
  const names = input.touched.length ? input.touched : Object.keys(input.fields);
  const rows: ChangeRow[] = names.flatMap((n) => {
    const said = input.summaries?.[n];
    const image = input.images?.[n] ?? null;
    if (said) {
      const { label, value } = splitSummary(said);
      return [{ label: label && pageLabel(n, label), value, image }];
    }
    const value = input.fields[n];
    if (n === 'look' && typeof value === 'string' && value.trim()) return [{ label: 'Look', value: lookName(value.trim()), image }];
    return n in input.fields ? fieldLines(n, value).map((line) => ({ ...splitSummary(line), image })) : [];
  });
  const image = rows.find((r) => r.image)?.image ?? null;
  const one = (r: ChangeRow) => (r.label ? `${r.label} → ${r.value}` : r.value);
  if (input.lock) return { what: 'You approved the plan', value: rows.length === 0 ? 'Build it' : rows.length === 1 ? one(rows[0]) : `with ${rows.length} changes`, image, rows };
  if (rows.length === 0) return { what: 'You sent the page', value: 'without changes', image, rows };
  if (rows.length === 1) {
    const [r] = rows;
    if (NOTE_LABELS.test(r.label) && r.value === 'cleared') return { what: 'You cleared your note', value: '', image, rows };
    if (isQuote(r) || NOTE_LABELS.test(r.label)) return { what: 'You wrote', value: r.label ? `“${r.value}”` : r.value, image, rows };
    if (r.label && /^(on|off)$/.test(r.value)) return { what: `You turned ${r.value}`, value: r.label, image, rows };
    return r.label ? { what: `You changed the ${r.label.toLowerCase()}`, value: r.value, image, rows } : { what: 'You asked for', value: r.value, image, rows };
  }
  const notes = rows.filter(isQuote).length;
  const named = rows.filter((r) => !isQuote(r)).map((r) => (r.label || r.value).toLowerCase());
  const value = listWords([...named, ...(notes ? [`${notes} note${notes === 1 ? '' : 's'}`] : [])]);
  return { what: `You changed ${rows.length} things`, value, image, rows };
}

/** How a later message answered Ivy's question, for "✓ You answered: …". */
export function answeredWords(text: string, find: (id: string) => ChangeSource | undefined): string {
  const { inputs, rest } = splitStoryboardInputs(text);
  if (inputs.length) {
    const first = find(inputs[0].id);
    if (!first) return /· lock\]/.test(text) ? 'approved the plan' : 'sent changes from the storyboard';
    const { what, value } = changeWords(first);
    if (what === 'You wrote') return 'wrote a note';
    if (/^You turned (on|off)$/.test(what)) return `${what.replace(/^You /, '')} ${value.toLowerCase()}`;
    return what.replace(/^You /, '');
  }
  const line = rest.split('\n')[0].trim();
  if (line.length <= 64) return line;
  return `${line.slice(0, 64).replace(/\s+\S*$/, '')}…`;
}

/** What the storyboard page is told Ivy is doing: applying while her turn answers a storyboard send. */
export function storyboardIvyState(messages: { type: string; role?: string; content: string }[], isStreaming: boolean): SbIvyState {
  if (!isStreaming) return 'idle';
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.type === 'chat' && m.role === 'user') return splitStoryboardInputs(m.content).inputs.length ? 'applying' : 'busy';
  }
  return 'busy';
}

const FRESH_MS = 2 * 60_000;

/** The open version is the one Ivy just made from a storyboard send (for the page's toast). */
export function freshVersion(inputs: StoryboardInputRecord[], versions: { version: number; createdAt: string }[], selected: number | null, now = Date.now()): boolean {
  const made = versions.find((v) => v.version === selected);
  if (!made || now - new Date(made.createdAt).getTime() > FRESH_MS) return false;
  return inputs.some((i) => appliedIn(i, versions) === selected);
}
